import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Car,
  Heart,
  Star,
  Gem,
  DollarSign,
  TrendingUp,
  Plus,
  Clock,
} from 'lucide-react';
import { useGetHotWheels } from '../../hooks/use-hotwheels';
import { StatCard } from '../../component/stat-card/stat-card';
import { HotWheelCard } from '../../component/hotwheel-card/hotwheel-card';

/**
 * Hot Wheels dashboard.
 *
 * Top row: 6 summary stat cards.
 * Second row: 3 mini bar charts (cars by year, brand, series).
 * Third row: "Recently added" carousel.
 *
 * All counts/values are computed client-side from the user's cars. No separate
 * stats endpoint — the list query already returns everything.
 */

export function HotWheelsDashboard() {
  const { t } = useTranslation();

  const { data, isLoading } = useGetHotWheels({ pageNo: 0, pageSize: 200 });
  const items = data?.items ?? [];

  const stats = useMemo(() => {
    const total = items.length;
    const favorites = items.filter((c) => c.Favorite).length;
    const treasureHunts = items.filter((c) => c.TreasureHunt && !c.SuperTreasureHunt).length;
    const superTreasureHunts = items.filter((c) => c.SuperTreasureHunt).length;
    const totalValue = items.reduce(
      (sum, c) => sum + (c.EstimatedValue ?? 0),
      0
    );
    const sortedByDate = [...items].sort(
      (a, b) =>
        new Date(b.CreatedDate ?? 0).getTime() -
        new Date(a.CreatedDate ?? 0).getTime()
    );
    const recentlyAdded = sortedByDate.slice(0, 4);

    return {
      total,
      favorites,
      treasureHunts,
      superTreasureHunts,
      totalValue,
      recentlyAdded,
    };
  }, [items]);

  // Charts data
  const yearData = useMemo(() => {
    const map: Record<number, number> = {};
    items.forEach((c) => {
      map[c.ReleaseYear] = (map[c.ReleaseYear] ?? 0) + 1;
    });
    return Object.entries(map)
      .map(([year, count]) => ({ year: Number(year), count }))
      .sort((a, b) => a.year - b.year);
  }, [items]);

  const brandData = useMemo(() => {
    const map: Record<string, number> = {};
    items.forEach((c) => {
      map[c.Brand] = (map[c.Brand] ?? 0) + 1;
    });
    return Object.entries(map)
      .map(([brand, count]) => ({ brand, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [items]);

  const seriesData = useMemo(() => {
    const map: Record<string, number> = {};
    items.forEach((c) => {
      map[c.Series] = (map[c.Series] ?? 0) + 1;
    });
    return Object.entries(map)
      .map(([series, count]) => ({ series, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [items]);

  const maxYearCount = Math.max(1, ...yearData.map((d) => d.count));
  const maxBrandCount = Math.max(1, ...brandData.map((d) => d.count));
  const maxSeriesCount = Math.max(1, ...seriesData.map((d) => d.count));

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">{t('DASHBOARD')}</h1>
          <p className="text-zinc-400 text-sm mt-1">
            Welcome to your garage.
          </p>
        </div>
        <Link
          to="/collection/new"
          className="rounded-md bg-[#2563EB] hover:bg-blue-600 text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 self-start md:self-auto"
        >
          <Plus className="h-4 w-4" /> {t('ADD_CAR')}
        </Link>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          icon={Car}
          label={t('TOTAL_CARS')}
          value={isLoading ? '…' : stats.total}
          accent="red"
        />
        <StatCard
          icon={Heart}
          label={t('FAVORITES')}
          value={isLoading ? '…' : stats.favorites}
          accent="red"
        />
        <StatCard
          icon={Star}
          label={t('TREASURE_HUNTS')}
          value={isLoading ? '…' : stats.treasureHunts}
          accent="gold"
        />
        <StatCard
          icon={Gem}
          label={t('SUPER_TREASURE_HUNTS')}
          value={isLoading ? '…' : stats.superTreasureHunts}
          accent="gold"
        />
        <StatCard
          icon={DollarSign}
          label={t('TOTAL_VALUE')}
          value={isLoading ? '…' : `$${stats.totalValue.toFixed(0)}`}
          accent="green"
        />
        <StatCard
          icon={TrendingUp}
          label={t('RECENTLY_ADDED')}
          value={isLoading ? '…' : stats.recentlyAdded.length}
          footer="last 4 shown below"
          accent="blue"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title={t('CARS_BY_YEAR')}>
          {yearData.length === 0 ? (
            <ChartEmpty />
          ) : (
            <div className="space-y-1.5">
              {yearData.map((d) => (
                <Bar
                  key={d.year}
                  label={String(d.year)}
                  value={d.count}
                  max={maxYearCount}
                  color="bg-blue-500"
                />
              ))}
            </div>
          )}
        </ChartCard>

        <ChartCard title={t('CARS_BY_BRAND')}>
          {brandData.length === 0 ? (
            <ChartEmpty />
          ) : (
            <div className="space-y-1.5">
              {brandData.map((d) => (
                <Bar
                  key={d.brand}
                  label={d.brand}
                  value={d.count}
                  max={maxBrandCount}
                  color="bg-yellow-500"
                />
              ))}
            </div>
          )}
        </ChartCard>

        <ChartCard title={t('CARS_BY_SERIES')}>
          {seriesData.length === 0 ? (
            <ChartEmpty />
          ) : (
            <div className="space-y-1.5">
              {seriesData.map((d) => (
                <Bar
                  key={d.series}
                  label={d.series}
                  value={d.count}
                  max={maxSeriesCount}
                  color="bg-emerald-500"
                />
              ))}
            </div>
          )}
        </ChartCard>
      </div>

      {/* Recently added */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Clock className="h-4 w-4 text-zinc-400" />
            {t('RECENTLY_ADDED')}
          </h2>
          <Link
            to="/collection"
            className="text-sm text-zinc-400 hover:text-white transition"
          >
            {t('MY_COLLECTION')} →
          </Link>
        </div>
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[4/3] rounded-2xl bg-zinc-900 animate-pulse"
              />
            ))}
          </div>
        ) : stats.recentlyAdded.length === 0 ? (
          <p className="text-zinc-500 text-sm">No cars yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.recentlyAdded.map((car) => (
              <HotWheelCard
                key={car.ItemId}
                car={car}
                onEdit={() => {}}
                onDelete={() => {}}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lightweight chart primitives (no charting library; CSS bars only).
// ---------------------------------------------------------------------------

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
      <h3 className="text-sm font-semibold text-white mb-4">{title}</h3>
      {children}
    </div>
  );
}

function ChartEmpty() {
  return (
    <div className="text-xs text-zinc-500 py-6 text-center">
      No data yet.
    </div>
  );
}

function Bar({
  label,
  value,
  max,
  color,
}: {
  label: string;
  value: number;
  max: number;
  color: string;
}) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="text-xs text-zinc-400 w-20 truncate" title={label}>
        {label}
      </div>
      <div className="flex-1 h-5 bg-zinc-800 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-xs font-semibold text-white w-6 text-right">{value}</div>
    </div>
  );
}